import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocale } from './LocaleContext.js';

type ToastVariant = 'default' | 'error';

export const TOAST_Z_INDEX = 10100;
export const DEFAULT_TOAST_DURATION_MS = 6000;
export const FIELD_SAVED_TOAST_DURATION_MS = 1500;

interface ToastContextValue {
  toast: React.ReactNode | null;
  variant: ToastVariant;
  showToast: (message: React.ReactNode) => void;
  showPersistentToast: (message: React.ReactNode, id: string) => void;
  showErrorToast: (message: React.ReactNode) => void;
  showFieldSavedToast: (fieldLabel: string) => void;
  hideToast: (id?: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastContainerProps {
  toast: React.ReactNode | null;
  variant: ToastVariant;
  hideToast: () => void;
  t: (key: string) => string;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toast, variant, hideToast, t }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (toast) {
      setShouldRender(true);
      setTimeout(() => setIsVisible(true), 10);
      return undefined;
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleRefresh = (): void => {
    window.location.reload();
  };

  if (!shouldRender) {
    return null;
  }

  const isError = variant === 'error';

  return (
    <>
      <style>{`
        @media (max-width: 640px) {
          .toast-container {
            right: 0.5rem !important;
            left: 0.5rem !important;
            bottom: 0.5rem !important;
            max-width: calc(100% - 1rem) !important;
            min-width: auto !important;
            padding: 0.875rem 1rem !important;
            font-size: 0.875rem !important;
          }
          .toast-text {
            margin-left: 0 !important;
            word-break: break-word;
          }
          .toast-refresh-button {
            padding: 0.25rem 0.5rem !important;
            font-size: 0.8125rem !important;
            white-space: nowrap;
          }
        }
      `}</style>
      <div
        className="toast-container"
        style={{
          position: 'fixed',
          right: '1rem',
          bottom: '1rem',
          backgroundColor: isError ? '#dc2626' : '#111827',
          color: '#ffffff',
          padding: isError ? '1.25rem 1.5rem' : '0.75rem 1rem',
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
          zIndex: TOAST_Z_INDEX,
          minWidth: isError ? '300px' : 'auto',
          maxWidth: 'calc(100% - 2rem)',
          fontSize: isError ? '1rem' : '0.875rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
          transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
        }}
        role="status"
        aria-live="polite"
      >
        <span className="toast-text" style={{ flex: 1, marginLeft: '0.5rem' }}>{toast}</span>
      {isError && (
        <button
          className="toast-refresh-button"
          onClick={handleRefresh}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: '#ffffff',
            cursor: 'pointer',
            padding: '0.375rem 0.75rem',
            borderRadius: '4px',
            fontSize: '0.875rem',
            fontWeight: 500,
            transition: 'background 0.2s',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
        >
          {t('refresh')}
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          hideToast();
        }}
        style={{
          background: 'none',
          border: 'none',
          color: '#ffffff',
          cursor: 'pointer',
          padding: '0.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.25rem',
          lineHeight: 1,
          opacity: 0.8,
          transition: 'opacity 0.2s',
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.8';
        }}
        aria-label={t('close')}
      >
        ×
      </button>
    </div>
    </>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useLocale();
  const [toast, setToast] = useState<React.ReactNode | null>(null);
  const [variant, setVariant] = useState<ToastVariant>('default');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const toastIdRef = useRef<string | null>(null);

  const clearTimer = useCallback((): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const hideToast = useCallback((id?: string): void => {
    if (typeof id === 'string' && toastIdRef.current !== id) {
      return;
    }
    clearTimer();
    toastIdRef.current = null;
    setToast(null);
  }, [clearTimer]);

  const showToast = useCallback((message: React.ReactNode, durationMs = DEFAULT_TOAST_DURATION_MS): void => {
    setToast(message);
    setVariant('default');
    toastIdRef.current = null;
    clearTimer();
    timeoutRef.current = setTimeout(() => {
      setToast(null);
      timeoutRef.current = null;
    }, durationMs);
  }, [clearTimer]);

  const showPersistentToast = useCallback((message: React.ReactNode, id: string): void => {
    setToast(message);
    setVariant('default');
    toastIdRef.current = id;
    clearTimer();
  }, [clearTimer]);

  const showErrorToast = useCallback((message: React.ReactNode): void => {
    setToast(message);
    setVariant('error');
    toastIdRef.current = null;
    clearTimer();
  }, [clearTimer]);

  const showFieldSavedToast = useCallback((fieldLabel: string): void => {
    showToast(
      t('inline_editable_field_saved').replace('{field}', fieldLabel),
      FIELD_SAVED_TOAST_DURATION_MS,
    );
  }, [showToast, t]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  const value = useMemo<ToastContextValue>(() => ({
    toast,
    variant,
    showToast,
    showPersistentToast,
    showErrorToast,
    showFieldSavedToast,
    hideToast,
  }), [toast, variant, showToast, showPersistentToast, showErrorToast, showFieldSavedToast, hideToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <ToastContainer toast={toast} variant={variant} hideToast={hideToast} t={t} />,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

